import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { testimonials } from "@/mocks/testimonials";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-sm ${star <= rating ? "ri-star-fill" : "ri-star-line"}`}
          style={star <= rating ? { color: "oklch(var(--accent-500))" } : { color: "oklch(var(--foreground-300))" }}
        ></i>
      ))}
    </div>
  );
}

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt");
    if (honeypot && typeof honeypot === "string" && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9quf04nlsngrm4lm6a0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};

      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || (parsed?.meta as Record<string, string>)?.detail
          || responseText
          || "";
        if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
          setFormError("提交失败，请稍后重试");
          setFormStatus("error");
        } else {
          setFormError(serverMsg || "提交失败，请稍后重试");
          setFormStatus("error");
        }
      }
    } catch {
      setFormError("网络异常，请稍后重试");
      setFormStatus("error");
    }
  };

  const values = [
    { icon: "ri-heart-line", title: t("about.value_user_first"), desc: t("about.value_user_first_desc") },
    { icon: "ri-lightbulb-line", title: t("about.value_innovation"), desc: t("about.value_innovation_desc") },
    { icon: "ri-award-line", title: t("about.value_quality"), desc: t("about.value_quality_desc") },
    { icon: "ri-global-line", title: t("about.value_open"), desc: t("about.value_open_desc") },
  ];

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "黄油网站",
    "url": "https://huangyou.design",
    "description": "黄油网站是专为创意人群打造的内容创作与设计平台",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "建国路88号SOHO现代城A座",
      "addressLocality": "北京市",
      "addressRegion": "朝阳区",
      "addressCountry": "CN",
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "400-888-8888",
      "contactType": "customer service",
      "email": "hello@huangyou.design",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "黄油网站是免费的吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "黄油网站提供免费版和专业版。免费版包含数千款基础模板和素材，足够日常使用。专业版解锁全部高级功能和专属资源。",
        },
      },
      {
        "@type": "Question",
        "name": "黄油网站的模板可以商用吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "是的，所有黄油网站模板和素材均提供商业使用授权，您可以放心用于商业项目。建议使用前查看具体模板的授权说明。",
        },
      },
      {
        "@type": "Question",
        "name": "黄油网站支持哪些导出格式？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "黄油网站支持导出PNG、JPG、PDF、SVG等多种格式，可满足不同场景的使用需求。专业版用户还可享受高清无压缩导出。",
        },
      },
      {
        "@type": "Question",
        "name": "如何联系黄油网站客服？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "您可以通过以下方式联系我们：发送邮件至hello@huangyou.design，拨打客服热线400-888-8888，或关注微信公众号\"黄油网站设计助手\"获取在线帮助。",
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}></script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Hero */}
          <section className="w-full py-12 md:py-20 bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="max-w-3xl">
                <Breadcrumb items={[
                  { label: "首页", to: "/" },
                  { label: t("about.title") },
                ]} />
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mt-4 md:mt-6" style={{ fontFamily: "var(--font-heading)" }}>
                  {t("about.title")}
                </h1>
                <p className="text-sm md:text-lg text-foreground-500 mt-3">
                  {t("about.subtitle")}
                </p>
              </div>
            </div>
          </section>

          {/* Story */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20team%20collaborating%20in%20bright%20minimal%20studio%20with%20warm%20lighting%2C%20designers%20working%20on%20laptops%20with%20mood%20boards%20on%20walls%2C%20clean%20scandinavian%20aesthetic%2C%20inspirational%20workspace%20atmosphere&width=800&height=600&seq=about-story-image&orientation=landscape"
                    alt="黄油网站团队"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 md:mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                    {t("about.story_title")}
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>{t("about.story_content_1")}</p>
                    <p>{t("about.story_content_2")}</p>
                    <p>{t("about.story_content_3")}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Mission */}
          <section className="w-full py-12 md:py-16" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full mb-5" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                <i className="ri-flag-line text-2xl" style={{ color: "oklch(var(--accent-500))" }}></i>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                {t("about.mission_title")}
              </h3>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                "{t("about.mission_content")}"
              </p>
            </div>
          </section>

          {/* Values */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 md:mb-10 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                {t("about.values_title")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {values.map((v, i) => (
                  <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center hover:border-background-300/60 transition-all duration-300">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full mb-4" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                      <i className={`${v.icon} text-lg`} style={{ color: "oklch(var(--accent-500))" }}></i>
                    </div>
                    <h4 className="text-base font-semibold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>{v.title}</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full py-14 md:py-20" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  {t("about.testimonials_title")}
                </h2>
                <p className="text-sm md:text-base text-foreground-500">{t("about.testimonials_subtitle")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="p-5 rounded-lg bg-background-50 border border-background-200/70 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                        <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{testimonial.name}</p>
                        <p className="text-xs text-foreground-500">{testimonial.role}</p>
                      </div>
                    </div>
                    <StarRating rating={testimonial.rating} />
                    <p className="text-sm text-foreground-600 leading-relaxed mt-3">{testimonial.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  {t("about.contact_title")}
                </h2>
                <p className="text-sm md:text-base text-foreground-500">{t("about.contact_subtitle")}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                {/* Contact Info */}
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                      <i className="ri-map-pin-line text-lg" style={{ color: "oklch(var(--accent-500))" }}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950 mb-0.5">{t("about.contact_address")}</p>
                      <p className="text-sm text-foreground-600">{t("about.address_value")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                      <i className="ri-mail-line text-lg" style={{ color: "oklch(var(--accent-500))" }}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950 mb-0.5">{t("about.contact_email")}</p>
                      <p className="text-sm text-foreground-600">{t("about.email_value")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                      <i className="ri-phone-line text-lg" style={{ color: "oklch(var(--accent-500))" }}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950 mb-0.5">{t("about.contact_phone")}</p>
                      <p className="text-sm text-foreground-600">{t("about.phone_value")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                      <i className="ri-wechat-line text-lg" style={{ color: "oklch(var(--accent-500))" }}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950 mb-0.5">{t("about.contact_wechat")}</p>
                      <p className="text-sm text-foreground-600">{t("about.wechat_value")}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="cf-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t("about.contact_form_name")} <span style={{ color: "oklch(var(--accent-500))" }}>*</span>
                        </label>
                        <input
                          id="cf-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-background-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t("about.contact_form_email")} <span style={{ color: "oklch(var(--accent-500))" }}>*</span>
                        </label>
                        <input
                          id="cf-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-background-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="cf-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t("about.contact_form_subject")}
                      </label>
                      <input
                        id="cf-subject"
                        name="subject"
                        type="text"
                        className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-background-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t("about.contact_form_message")} <span style={{ color: "oklch(var(--accent-500))" }}>*</span>
                      </label>
                      <textarea
                        id="cf-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-background-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <div className="h-0 overflow-hidden opacity-0 pointer-events-none absolute">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      className="text-sm text-background-50 px-6 py-2.5 rounded-md font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-90 self-start"
                      style={{ backgroundColor: "oklch(var(--accent-500))" }}
                    >
                      {t("about.contact_form_submit")}
                    </button>

                    {formStatus === "success" && (
                      <p className="text-sm text-green-600">{t("about.contact_form_success")}</p>
                    )}
                    {formStatus === "error" && (
                      <p className="text-sm" style={{ color: "oklch(var(--accent-500))" }}>{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full py-14 md:py-20" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 md:mb-10 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <div className="space-y-4">
                <details className="group rounded-lg bg-background-50 border border-background-200/70 cursor-pointer">
                  <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-950 list-none">
                    黄油网站是免费的吗？
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    黄油网站提供免费版和专业版。免费版包含数千款基础模板和素材，足够日常使用。专业版解锁全部高级功能和专属资源。
                  </p>
                </details>
                <details className="group rounded-lg bg-background-50 border border-background-200/70 cursor-pointer">
                  <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-950 list-none">
                    黄油网站的模板可以商用吗？
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    是的，所有黄油网站模板和素材均提供商业使用授权，您可以放心用于商业项目。建议使用前查看具体模板的授权说明。
                  </p>
                </details>
                <details className="group rounded-lg bg-background-50 border border-background-200/70 cursor-pointer">
                  <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-950 list-none">
                    黄油网站支持哪些导出格式？
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    黄油网站支持导出PNG、JPG、PDF、SVG等多种格式，可满足不同场景的使用需求。
                  </p>
                </details>
                <details className="group rounded-lg bg-background-50 border border-background-200/70 cursor-pointer">
                  <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-950 list-none">
                    如何联系黄油网站客服？
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    您可以通过以下方式联系我们：发送邮件至hello@huangyou.design，拨打客服热线400-888-8888，或关注微信公众号"黄油网站设计助手"获取在线帮助。
                  </p>
                </details>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}